import { useMemo } from "react"
import useTranslator from "@pma/hooks/use-translator"
import { cn } from "@pma/utils/helpers"

interface Props {
  status: string
  isApproved?: boolean
  isSigned?: boolean
  isDeclined?: boolean
  isReturnedToContractor?: boolean
  size?: "sm" | "md"
}

const STEPS_KEYS = ["submitted", "in approval", "for signing", "signed", "paid"] as const

export function BillLifecycleStepper({
  status,
  isApproved = false,
  isSigned = false,
  isDeclined = false,
  isReturnedToContractor = false,
  size = "md",
}: Props) {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  const activeStep = useMemo(() => {
    if (isDeclined) return -1 // special
    if (isReturnedToContractor) return -2 // special
    if (isSigned && status === "signed") return 4 // paid
    if (isSigned) return 3 // signed
    if (isApproved && !isSigned) return 2 // for signing
    if (status === "pending") return 1 // in approval
    return 0 // submitted (new)
  }, [status, isApproved, isSigned, isDeclined, isReturnedToContractor])

  // Determine which step is "current" for declined state
  const declinedStep = useMemo(() => {
    if (!isDeclined) return -1
    if (status === "pending") return 1
    if (status === "approved") return 2
    return 1
  }, [isDeclined, status])

  // For returned, highlight step 0
  const returnedStep = isReturnedToContractor ? 0 : -1

  const isSm = size === "sm"
  const dotSize = isSm ? "w-2 h-2" : "w-3 h-3"
  const lineHeight = isSm ? "h-0.5" : "h-0.5"
  const gap = isSm ? "gap-0" : "gap-1"

  return (
    <div
      className={cn("flex items-center", isSm ? "gap-0.5" : "gap-0")}
      dir={isHebrew ? "rtl" : "ltr"}
    >
      {STEPS_KEYS.map((stepKey, index) => {
        const isCompleted =
          activeStep >= 0 && index < activeStep
        const isCurrent = activeStep >= 0 && index === activeStep
        const isFuture = activeStep >= 0 && index > activeStep
        const isDeclinedHere = isDeclined && index === declinedStep
        const isReturnedHere = isReturnedToContractor && index === returnedStep

        // Determine dot color
        let dotClasses = "bg-gray-200" // default future
        if (isDeclinedHere) {
          dotClasses = "bg-red-500"
        } else if (isReturnedHere) {
          dotClasses = "bg-orange-400"
        } else if (isCompleted) {
          dotClasses = "bg-emerald-500"
        } else if (isCurrent) {
          dotClasses = "bg-blue-500 ring-2 ring-blue-300 animate-pulse"
        }

        // Determine line color (line AFTER this dot)
        let lineClasses = "bg-gray-200"
        if (isCompleted) {
          lineClasses = "bg-emerald-500"
        }

        const isLast = index === STEPS_KEYS.length - 1

        return (
          <div
            key={stepKey}
            className={cn(
              "flex items-center",
              !isLast && "flex-1",
              isSm ? "" : "flex-col",
            )}
          >
            {isSm ? (
              // Small: horizontal dots + lines inline
              <div className='flex items-center w-full'>
                <div className={cn("rounded-full shrink-0", dotSize, dotClasses)} />
                {!isLast && (
                  <div className={cn("flex-1 min-w-1", lineHeight, lineClasses)} />
                )}
              </div>
            ) : (
              // Medium: dot + label, with connecting lines
              <div className='flex items-center w-full'>
                <div className='flex flex-col items-center'>
                  <div className={cn("rounded-full shrink-0", dotSize, dotClasses)} />
                  <span
                    className={cn(
                      "mt-1 text-[10px] leading-tight whitespace-nowrap",
                      isCurrent
                        ? "text-blue-600 font-medium"
                        : isCompleted
                          ? "text-emerald-600"
                          : isDeclinedHere
                            ? "text-red-600 font-medium"
                            : isReturnedHere
                              ? "text-orange-600 font-medium"
                              : "text-gray-400",
                    )}
                  >
                    {isDeclinedHere
                      ? translate("declined")
                      : isReturnedHere
                        ? translate("returned")
                        : translate(stepKey)}
                  </span>
                </div>
                {!isLast && (
                  <div className={cn("flex-1 min-w-2 mt-[-12px]", lineHeight, lineClasses)} />
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
